import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { NavOverlay } from '@/components/NavOverlay';
import { useResolveUserId } from '@/hooks/useResolveUserId';
import { usePageTitle } from '@/hooks/usePageTitle';

function renderNode(node: any): string {
  if (!node) return '';
  if (node.type === 'text') {
    let text = (node.text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (node.marks) {
      for (const mark of node.marks) {
        if (mark.type === 'bold') text = `<strong>${text}</strong>`;
        if (mark.type === 'italic') text = `<em>${text}</em>`;
        if (mark.type === 'link') text = `<a href="${mark.attrs?.href || '#'}" target="_blank" rel="noopener">${text}</a>`;
      }
    }
    return text;
  }
  const children = (node.content || []).map(renderNode).join('');
  switch (node.type) {
    case 'doc': return children;
    case 'paragraph': return `<p>${children}</p>`;
    case 'heading': return `<h${node.attrs?.level || 2}>${children}</h${node.attrs?.level || 2}>`;
    case 'bulletList': return `<ul>${children}</ul>`;
    case 'orderedList': return `<ol>${children}</ol>`;
    case 'listItem': return `<li>${children}</li>`;
    case 'blockquote': return `<blockquote>${children}</blockquote>`;
    case 'codeBlock': return `<pre><code>${children}</code></pre>`;
    case 'hardBreak': return '<br />';
    case 'horizontalRule': return '<hr />';
    case 'image': return `<img src="${node.attrs?.src || ''}" alt="${node.attrs?.alt || ''}" />`;
    default: return children;
  }
}

export default function FreePagePublic() {
  const { pageSlug } = useParams<{ pageSlug: string }>();
  const { userId } = useResolveUserId();
  const [html, setHtml] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  usePageTitle(title || null);

  useEffect(() => {
    if (!userId || !pageSlug) {
      if (!pageSlug) { setNotFound(true); setLoading(false); }
      return;
    }

    const load = async () => {
      const { data } = await supabase
        .from('free_pages')
        .select('title, content_json, published')
        .eq('slug', pageSlug)
        .eq('user_id', userId)
        .maybeSingle();

      if (!data) {
        setNotFound(true);
      } else {
        setTitle((data as any).title);
        try {
          const content = (data as any).content_json;
          if (content && content.type === 'doc') {
            setHtml(renderNode(content));
          }
        } catch {
          setHtml('<p>Erro ao carregar conteúdo.</p>');
        }
      }
      setLoading(false);
    };
    load();
  }, [pageSlug, userId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, opacity: 0.4 }}>Carregando...</span>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 14 }}>Página não encontrada.</span>
      </div>
    );
  }

  return (
    <>
      <NavOverlay />
      <div className="min-h-screen bg-background overflow-auto pt-24 pb-16 px-8">
        <div className="max-w-2xl mx-auto">
          <h1
            className="uppercase tracking-wider mb-8"
            style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 16, fontWeight: 300 }}
          >
            {title}
          </h1>
          <div
            className="free-page-content"
            style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 15, fontWeight: 300, lineHeight: 1.8 }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </div>
    </>
  );
}
